import { AccountBalanceWallet, Block, Close, Settings } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Typography,
} from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ApiQuestEntryLog, ApiQuestEntryStaff } from "griptonite-api-client/types";
import { ReactNode, useState } from "react";

import { useQuestEntriesQuery, useQuestEntryLogsQuery, useQuestQuery } from "@/hooks/quests";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";
import pluralize from "@/utils/pluralize";

import Avatar from "../Avatar";
import CopyButton from "../CopyButton";
import { ButtonLink } from "../Links";
import { CreditAmount } from "../Loyalty/SortableTable";
import LeaderboardBanModal from "./LeaderboardBanModal";
import { QuestEntryStatusChip } from "./QuestEntries";

const formatTime = (timestamp: string) => format(new Date(timestamp), "d MMM yyyy, HH:mm");

export const CAP_REASON_LABELS: Record<string, string> = {
  ACCOUNT_BALANCE: "the account’s balance limit",
  OUTSTANDING_CREDITS: "the gym’s outstanding credits limit",
};

// Pills as in the mobile app: fully rounded, with bold caption text. Boosts are blue and side
// quests grey.
export const LogPill = ({ tone, children }: { tone: "blue" | "grey"; children: ReactNode }) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      px: 1,
      py: "2px",
      borderRadius: "999px",
      fontSize: 12,
      fontWeight: "lg",
      lineHeight: "16px",
      color: tone === "blue" ? "#4579FF" : "neutral.700",
      backgroundColor: tone === "blue" ? "#E8F2FE" : "#F1F1F4",
    }}
  >
    {children}
  </Box>
);

// The summary above the time, any pills beside the time, and the signed amount at the end, as in
// the mobile app's quest log. A cap note gets its own row underneath.
const LogRow = ({ log }: { log: ApiQuestEntryLog }) => (
  <Stack sx={{ px: 2, py: 1.5 }} gap={0.5}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography level="title-sm" fontWeight="lg">
          {log.summary}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
          {log.multiplier !== null ? <LogPill tone="blue">×{log.multiplier} boost</LogPill> : null}
          {log.kind === "BONUS" ? <LogPill tone="grey">Side quest</LogPill> : null}
          <Typography level="body-xs" textColor="neutral.500">
            {formatTime(log.createdAt)}
          </Typography>
        </Stack>
      </Box>
      <CreditAmount amount={log.amount} />
    </Stack>
    {log.requestedAmount !== null ? (
      <Typography level="body-xs" textColor="neutral.500">
        Capped: {pluralize(log.requestedAmount, "credit")} were due
        {log.capReason ? `, but ${CAP_REASON_LABELS[log.capReason] ?? log.capReason} applied` : ""}
      </Typography>
    ) : null}
  </Stack>
);

const LogList = ({ questUuid, entryUuid }: { questUuid: string; entryUuid: string }) => {
  const query = useQuestEntryLogsQuery(questUuid, entryUuid);

  if (query.error) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Couldn’t load the log.
      </Alert>
    );
  }
  if (!query.data) {
    return <CircularProgress sx={{ alignSelf: "center", mt: 4 }} />;
  }
  const logs = query.data.pages.flatMap((page) => page.data);
  if (!logs.length) {
    return (
      <Typography textColor="neutral.500" sx={{ p: 2 }}>
        No credits earned yet.
      </Typography>
    );
  }
  return (
    <Stack divider={<Divider />}>
      {logs.map((log) => (
        <LogRow key={log.uuid} log={log} />
      ))}
      {query.hasNextPage ? (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Button
            variant="outlined"
            color="neutral"
            loading={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            Load more
          </Button>
        </Box>
      ) : null}
    </Stack>
  );
};

// Who the entrant is, under their name, as on the account panel. The link to their credit account
// is missing until their first grant creates it.
const EntrantInfo = ({ entry, venueUuid }: { entry: ApiQuestEntryStaff; venueUuid: string }) => (
  <Stack gap={0.75}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
        <Typography level="body-sm" sx={{ overflowWrap: "anywhere" }}>
          {entry.user.email}
        </Typography>
        <CopyButton value={entry.user.email} label="email" />
      </Stack>
      {entry.creditAccountUuid ? (
        <ButtonLink
          to="/gym/$venueUuid/loyalty/accounts/$accountUuid"
          params={{ venueUuid, accountUuid: entry.creditAccountUuid }}
          size="sm"
          variant="outlined"
          color="neutral"
          startDecorator={<AccountBalanceWallet sx={{ fontSize: 16 }} />}
          // Joy has no xs button, so shrink sm by hand.
          sx={{
            flexShrink: 0,
            minHeight: 24,
            px: 1,
            py: 0,
            fontSize: "xs",
            "--Button-gap": "4px",
          }}
        >
          View account
        </ButtonLink>
      ) : null}
    </Stack>
    {entry.user.parent ? (
      <Typography level="body-sm" textColor="neutral.500">
        Managed by {entry.user.parent.name}
      </Typography>
    ) : null}
    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
      {entry.leaderboardBanned ? (
        <Chip size="sm" variant="soft" color="danger" startDecorator={<Block fontSize="small" />}>
          Banned from quest
        </Chip>
      ) : (
        <QuestEntryStatusChip status={entry.status} />
      )}
      <Typography level="body-sm" textColor="neutral.500">
        Joined {formatTime(entry.creationTime)}
        {entry.completionTime ? ` · Completed ${formatTime(entry.completionTime)}` : null}
      </Typography>
    </Stack>
  </Stack>
);

const Stat = ({ label, value }: { label: string; value: number }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography level="body-sm" textColor="neutral.500">
      {label}
    </Typography>
    <Typography level="h3">{value.toLocaleString()}</Typography>
  </Box>
);

// Points and milestones only mean something on a quest that scores points.
const EntrySummary = ({ entry }: { entry: ApiQuestEntryStaff }) => (
  <Stack
    direction="row"
    gap={2}
    sx={{ p: 2, borderBottom: "1px solid", borderColor: "neutral.200" }}
  >
    <Stat label="Credits earned" value={entry.creditsEarned} />
    {entry.score !== null ? (
      <>
        <Stat label="Points" value={entry.score} />
        <Stat label="Milestones" value={entry.thresholdsReached.length} />
      </>
    ) : null}
  </Stack>
);

const EntryMenu = ({ onBan }: { onBan: () => void }) => (
  <Dropdown>
    <MenuButton
      aria-label="Entry actions"
      slots={{ root: IconButton }}
      slotProps={{ root: { color: "neutral", variant: "plain" } }}
    >
      <Settings />
    </MenuButton>
    <Menu size="sm" placement="bottom-end">
      <MenuItem color="danger" onClick={onBan}>
        Remove from quest
      </MenuItem>
    </Menu>
  </Dropdown>
);

export default function QuestEntryPanel({
  questUuid,
  entryUuid,
}: {
  questUuid: string;
  entryUuid: string;
}) {
  const navigate = useNavigate();
  const venueUuid = useCurrentVenueUuid();
  const { data: venue } = useCurrentVenueQuery();
  const { data: quest } = useQuestQuery(questUuid, venueUuid);
  // There's no endpoint for one entry, so it comes from the list the table already loaded.
  const entriesQuery = useQuestEntriesQuery(questUuid);
  const entry = entriesQuery.data?.find((item) => item.uuid === entryUuid);
  const [isBanning, setIsBanning] = useState(false);
  // Only admins can ban, and a ban applies to the month it's made in, so it only disqualifies the
  // climber from a points quest that's running now.
  const canBan =
    venue?.userRole === "ADMIN" &&
    quest?.type === "LEADERBOARD_POINTS" &&
    quest.status === "ACTIVE";

  let content;
  if (entriesQuery.error) {
    content = (
      <Alert color="danger" sx={{ m: 2 }}>
        Couldn’t load the entry.
      </Alert>
    );
  } else if (entriesQuery.data && !entry) {
    content = (
      <Alert color="neutral" sx={{ m: 2 }}>
        This entry doesn’t exist.
      </Alert>
    );
  } else if (entry) {
    content = (
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <EntrySummary entry={entry} />
        <LogList questUuid={questUuid} entryUuid={entry.uuid} />
      </Box>
    );
  } else {
    content = <CircularProgress sx={{ alignSelf: "center", mt: 4 }} />;
  }

  return (
    <Stack
      sx={{
        width: 420,
        flexShrink: 0,
        backgroundColor: "white",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <Stack gap={1} sx={{ p: 2, borderBottom: "1px solid", borderColor: "neutral.200" }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          {entry ? <Avatar user={entry.user} size={32} /> : null}
          <Typography level="title-lg" noWrap sx={{ flex: 1 }}>
            {entry?.user.name ?? "Entry"}
          </Typography>
          {entry && canBan && !entry.leaderboardBanned ? (
            <EntryMenu onBan={() => setIsBanning(true)} />
          ) : null}
          <IconButton
            aria-label="Close"
            onClick={() =>
              navigate({
                to: "/gym/$venueUuid/loyalty/quests/$questUuid/entries",
                params: { venueUuid: venueUuid ?? "", questUuid },
              })
            }
          >
            <Close />
          </IconButton>
        </Stack>
        {entry ? <EntrantInfo entry={entry} venueUuid={venueUuid ?? ""} /> : null}
      </Stack>
      {content}
      {entry && quest && isBanning ? (
        <LeaderboardBanModal
          entry={entry}
          questName={quest.name}
          venueUuid={venueUuid ?? ""}
          questUuid={questUuid}
          onClose={() => setIsBanning(false)}
        />
      ) : null}
    </Stack>
  );
}
